import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    View, Image, StyleSheet, Animated, TouchableOpacity,
    Text, Dimensions, AppState, Modal, FlatList
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const { width: SW } = Dimensions.get('window');

// ── Animation engines ────────────────────────────────────────────────────────

/**
 * Crossfade: stacked images, opacity fades between them.
 */
const useCrossfadeEngine = (count, durationPerSlide, transitionDuration, playing, onFinish) => {
    const opacities = useRef(
        Array.from({ length: count }, (_, i) => new Animated.Value(i === 0 ? 1 : 0))
    ).current;
    const frameRef = useRef(0);
    const loopRef = useRef(null);

    const step = useCallback(() => {
        if (!playing) return;
        const curr = frameRef.current;
        const next = curr + 1;

        if (next >= count) {
            onFinish?.();
            return;
        }

        loopRef.current = Animated.sequence([
            Animated.delay(durationPerSlide - transitionDuration),
            Animated.parallel([
                Animated.timing(opacities[next], { toValue: 1, duration: transitionDuration, useNativeDriver: true }),
                Animated.timing(opacities[curr], { toValue: 0, duration: transitionDuration, useNativeDriver: true }),
            ]),
        ]);
        loopRef.current.start(({ finished }) => {
            if (finished) {
                frameRef.current = next;
                step();
            }
        });
    }, [count, durationPerSlide, transitionDuration, playing, opacities, onFinish]);

    useEffect(() => {
        if (playing) step();
        return () => loopRef.current?.stop();
    }, [playing, step]);

    return opacities;
};

/**
 * Ken Burns: each frame crossfades IN and slowly zooms + pans.
 * Randomises pan direction per frame for cinematic variety.
 */
const useKenBurnsEngine = (count, durationPerSlide, transitionDuration, playing, onFinish) => {
    const opacities = useRef(Array.from({ length: count }, (_, i) => new Animated.Value(i === 0 ? 1 : 0))).current;
    const scales   = useRef(Array.from({ length: count }, () => new Animated.Value(1.0))).current;
    const transX   = useRef(Array.from({ length: count }, () => new Animated.Value(0))).current;
    const transY   = useRef(Array.from({ length: count }, () => new Animated.Value(0))).current;
    const frameRef = useRef(0);
    const loopRef  = useRef(null);

    const panDirections = [
        { dx: 6, dy: 4 }, { dx: -6, dy: -4 }, { dx: 4, dy: -6 }, { dx: -4, dy: 6 },
        { dx: 0, dy: -7 }, { dx: 0, dy: 7 },
    ];

    const step = useCallback(() => {
        if (!playing) return;
        const curr = frameRef.current;
        const next = curr + 1;

        if (next >= count) {
            onFinish?.();
            return;
        }

        const pan = panDirections[curr % panDirections.length];
        scales[next].setValue(1.0);
        transX[next].setValue(0);
        transY[next].setValue(0);

        loopRef.current = Animated.sequence([
            Animated.parallel([
                Animated.timing(scales[curr], { toValue: 1.035, duration: durationPerSlide, useNativeDriver: true }),
                Animated.timing(transX[curr], { toValue: pan.dx, duration: durationPerSlide, useNativeDriver: true }),
                Animated.timing(transY[curr], { toValue: pan.dy, duration: durationPerSlide, useNativeDriver: true }),
            ]),
        ]);

        const fadePoint = Animated.sequence([
            Animated.delay(durationPerSlide - transitionDuration),
            Animated.parallel([
                Animated.timing(opacities[next], { toValue: 1, duration: transitionDuration, useNativeDriver: true }),
                Animated.timing(opacities[curr], { toValue: 0, duration: transitionDuration, useNativeDriver: true }),
            ]),
        ]);

        Animated.parallel([loopRef.current, fadePoint]).start(({ finished }) => {
            if (finished) {
                frameRef.current = next;
                step();
            }
        });
    }, [count, durationPerSlide, transitionDuration, playing, opacities, scales, transX, transY, onFinish]);

    useEffect(() => {
        if (playing) step();
        return () => loopRef.current?.stop();
    }, [playing, step]);

    return { opacities, scales, transX, transY };
};

/**
 * Slide: frames translate horizontally in/out.
 */
const useSlideEngine = (count, durationPerSlide, transitionDuration, playing, onFinish) => {
    const translateXs = useRef(
        Array.from({ length: count }, (_, i) => new Animated.Value(i === 0 ? 0 : SW))
    ).current;
    const frameRef = useRef(0);
    const loopRef  = useRef(null);

    const step = useCallback(() => {
        if (!playing) return;
        const curr = frameRef.current;
        const next = curr + 1;

        if (next >= count) {
            onFinish?.();
            return;
        }

        translateXs[next].setValue(SW);

        loopRef.current = Animated.sequence([
            Animated.delay(durationPerSlide - transitionDuration),
            Animated.parallel([
                Animated.timing(translateXs[next], { toValue: 0, duration: transitionDuration, useNativeDriver: true }),
                Animated.timing(translateXs[curr], { toValue: -SW, duration: transitionDuration, useNativeDriver: true }),
            ]),
        ]);
        loopRef.current.start(({ finished }) => {
            if (finished) {
                frameRef.current = next;
                step();
            }
        });
    }, [count, durationPerSlide, transitionDuration, playing, translateXs, onFinish]);

    useEffect(() => {
        if (playing) step();
        return () => loopRef.current?.stop();
    }, [playing, step]);

    return translateXs;
};

/**
 * Zoom Burst: each frame scales 1.0 → 1.08 while held, then crossfades.
 */
const useZoomBurstEngine = (count, durationPerSlide, transitionDuration, playing, onFinish) => {
    const opacities = useRef(Array.from({ length: count }, (_, i) => new Animated.Value(i === 0 ? 1 : 0))).current;
    const scales    = useRef(Array.from({ length: count }, () => new Animated.Value(1.0))).current;
    const frameRef  = useRef(0);
    const loopRef   = useRef(null);

    const step = useCallback(() => {
        if (!playing) return;
        const curr = frameRef.current;
        const next = curr + 1;

        if (next >= count) {
            onFinish?.();
            return;
        }

        scales[next].setValue(1.0);

        const zoom   = Animated.timing(scales[curr], { toValue: 1.05, duration: durationPerSlide, useNativeDriver: true });
        const fade   = Animated.sequence([
            Animated.delay(durationPerSlide - transitionDuration),
            Animated.parallel([
                Animated.timing(opacities[next], { toValue: 1, duration: transitionDuration, useNativeDriver: true }),
                Animated.timing(opacities[curr], { toValue: 0, duration: transitionDuration, useNativeDriver: true }),
            ]),
        ]);

        loopRef.current = Animated.parallel([zoom, fade]);
        loopRef.current.start(({ finished }) => {
            if (finished) {
                scales[curr].setValue(1.0);
                frameRef.current = next;
                step();
            }
        });
    }, [count, durationPerSlide, transitionDuration, playing, opacities, scales, onFinish]);

    useEffect(() => {
        if (playing) step();
        return () => loopRef.current?.stop();
    }, [playing, step]);

    return { opacities, scales };
};

// ── Current-frame tracker (shared across engines) ────────────────────────────
const useFrameTracker = (count, durationPerSlide, playing = true, isFinished = false) => {
    const [frame, setFrame] = useState(0);
    useEffect(() => {
        if (!playing || isFinished) return;
        const interval = setInterval(() => {
            setFrame(f => {
                if (f >= count - 1) {
                    clearInterval(interval);
                    return f;
                }
                return f + 1;
            });
        }, durationPerSlide);
        return () => clearInterval(interval);
    }, [count, durationPerSlide, playing, isFinished]);
    return frame;
};

// ── Renderers ────────────────────────────────────────────────────────────────

const CrossfadeRenderer = ({ images, width, height, durationPerSlide, transitionDuration, playing, onFinish }) => {
    const opacities = useCrossfadeEngine(images.length, durationPerSlide, transitionDuration, playing, onFinish);
    return (
        <View style={{ width, height }}>
            {images.map((uri, i) => (
                <View key={i} style={StyleSheet.absoluteFill}>
                    <Animated.Image source={{ uri }} style={[StyleSheet.absoluteFill, { opacity: opacities[i], transform: [{ scale: 1.1 }] }]} blurRadius={15} resizeMode="cover" />
                    <Animated.Image source={{ uri }} style={[StyleSheet.absoluteFill, { opacity: opacities[i] }]} resizeMode="contain" />
                </View>
            ))}
        </View>
    );
};

const KenBurnsRenderer = ({ images, width, height, durationPerSlide, transitionDuration, playing, onFinish }) => {
    const { opacities, scales, transX, transY } = useKenBurnsEngine(images.length, durationPerSlide, transitionDuration, playing, onFinish);
    return (
        <View style={{ width, height, overflow: 'hidden' }}>
            {images.map((uri, i) => (
                <View key={i} style={StyleSheet.absoluteFill}>
                    <Animated.Image
                        source={{ uri }}
                        style={[StyleSheet.absoluteFill, { opacity: opacities[i], transform: [{ scale: 1.2 }] }]}
                        blurRadius={20}
                        resizeMode="cover"
                    />
                    <Animated.Image
                        source={{ uri }}
                        style={[StyleSheet.absoluteFill, {
                            opacity: opacities[i],
                            transform: [{ scale: scales[i] }, { translateX: transX[i] }, { translateY: transY[i] }],
                        }]}
                        resizeMode="contain"
                    />
                </View>
            ))}
        </View>
    );
};

const SlideRenderer = ({ images, width, height, durationPerSlide, transitionDuration, playing, onFinish }) => {
    const translateXs = useSlideEngine(images.length, durationPerSlide, transitionDuration, playing, onFinish);
    return (
        <View style={{ width, height, overflow: 'hidden' }}>
            {images.map((uri, i) => (
                <View key={i} style={StyleSheet.absoluteFill}>
                    <Animated.Image source={{ uri }} style={[StyleSheet.absoluteFill, { transform: [{ translateX: translateXs[i] }, { scale: 1.1 }] }]} blurRadius={15} resizeMode="cover" />
                    <Animated.Image source={{ uri }} style={[StyleSheet.absoluteFill, { transform: [{ translateX: translateXs[i] }] }]} resizeMode="contain" />
                </View>
            ))}
        </View>
    );
};

const ZoomBurstRenderer = ({ images, width, height, durationPerSlide, transitionDuration, playing, onFinish }) => {
    const { opacities, scales } = useZoomBurstEngine(images.length, durationPerSlide, transitionDuration, playing, onFinish);
    return (
        <View style={{ width, height, overflow: 'hidden' }}>
            {images.map((uri, i) => (
                <View key={i} style={StyleSheet.absoluteFill}>
                    <Animated.Image source={{ uri }} style={[StyleSheet.absoluteFill, { opacity: opacities[i], transform: [{ scale: 1.15 }] }]} blurRadius={15} resizeMode="cover" />
                    <Animated.Image
                        source={{ uri }}
                        style={[StyleSheet.absoluteFill, {
                            opacity: opacities[i],
                            transform: [{ scale: scales[i] }],
                        }]}
                        resizeMode="contain"
                    />
                </View>
            ))}
        </View>
    );
};

// ── Main PhotoClipPlayer ─────────────────────────────────────────────────────
/**
 * PhotoClipPlayer — renders an animated photo slideshow in the feed.
 * Zero video overhead: pure React Native Animated API with useNativeDriver.
 *
 * Props:
 *   images          string[]   Array of image URIs
 *   animationConfig object     { style, durationPerSlide, transitionDuration }
 *   width           number     Player width
 *   height          number     Player height
 *   borderRadius    number     Corner radius (default 12)
 *   autoPlay        boolean    Auto-start on mount (default true)
 */
const PhotoClipPlayer = ({
    images = [],
    animationConfig = {},
    width,
    height,
    borderRadius = 12,
    autoPlay = true,
}) => {
    const {
        style = 'kenBurns',
        durationPerSlide = 2000,
        transitionDuration = 600,
    } = animationConfig;

    const [appState, setAppState] = useState(AppState.currentState);
    const [playing, setPlaying] = useState(autoPlay);
    const [isFinished, setIsFinished] = useState(false);
    const [lightboxVisible, setLightboxVisible] = useState(false);

    useEffect(() => {
        if (!autoPlay || appState !== 'active' || isFinished) {
            setPlaying(false);
        } else {
            setPlaying(true);
        }
    }, [autoPlay, appState, isFinished]);

    useEffect(() => {
        const subscription = AppState.addEventListener('change', nextAppState => {
            setAppState(nextAppState);
        });
        return () => subscription.remove();
    }, []);

    const currentFrame = useFrameTracker(images.length, durationPerSlide, playing, isFinished);

    if (!images || images.length === 0) return null;

    const rendererProps = { 
        images, width, height, durationPerSlide, transitionDuration, 
        playing, onFinish: () => setIsFinished(true) 
    };

    const renderAnimation = () => {
        if (isFinished) return <CollageView images={images} width={width} height={height} />;
        
        switch (style) {
            case 'crossfade':   return <CrossfadeRenderer {...rendererProps} />;
            case 'slide':       return <SlideRenderer {...rendererProps} />;
            case 'zoomBurst':   return <ZoomBurstRenderer {...rendererProps} />;
            case 'kenBurns':
            default:            return <KenBurnsRenderer {...rendererProps} />;
        }
    };

    const totalMs = images.length * durationPerSlide;
    const fmtMs = (ms) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;

    return (
        <View style={{ width, height, borderRadius, overflow: 'hidden', backgroundColor: '#000' }}>
            {renderAnimation()}

            {/* Tap to toggle/lightbox */}
            <TouchableOpacity
                style={StyleSheet.absoluteFillObject}
                activeOpacity={1}
                onPress={() => isFinished ? setLightboxVisible(true) : setPlaying(p => !p)}
            />

            {/* Pause overlay */}
            {!playing && !isFinished && (
                <View style={p.pauseOverlay} pointerEvents="none">
                    <View style={p.pauseOrb}>
                        <Ionicons name="play" size={28} color="#fff" />
                    </View>
                </View>
            )}

            {/* Replay indicator if finished */}
            {isFinished && (
                <TouchableOpacity style={p.replayBtn} onPress={() => { setIsFinished(false); setPlaying(true); }}>
                    <Ionicons name="refresh" size={16} color="#fff" />
                    <Text style={p.replayText}>Replay</Text>
                </TouchableOpacity>
            )}

            {/* Top-right: image count */}
            <View style={p.topRight}>
                <View style={p.countBadge}>
                    <Ionicons name="images-outline" size={10} color="#fff" />
                    <Text style={p.countText}>{isFinished ? images.length : (currentFrame + 1)}/{images.length}</Text>
                </View>
            </View>

            {/* Bottom-left: duration */}
            {!isFinished && (
                <View style={p.bottomLeft}>
                    <View style={p.timeBadge}>
                        <Ionicons name="film" size={9} color="#a78bfa" />
                        <Text style={p.timeText}>{fmtMs(totalMs)}</Text>
                    </View>
                </View>
            )}

            {/* Bottom: frame dots */}
            {!isFinished && (
                <View style={p.dotsRow}>
                    {images.map((_, i) => (
                        <View
                            key={i}
                            style={[
                                p.dot,
                                i === currentFrame ? p.dotActive : p.dotInactive,
                            ]}
                        />
                    ))}
                </View>
            )}

            <SwipeableLightbox 
                visible={lightboxVisible} 
                images={images} 
                onClose={() => setLightboxVisible(false)} 
            />
        </View>
    );
};

// ── New Components ──────────────────────────────────────────────────────────

const CollageView = ({ images, width, height }) => {
    const count = images.length;
    const gap = 2;
    
    const img = (uri, w, h) => (
        <Image source={{ uri }} style={{ width: w, height: h }} resizeMode="cover" />
    );

    if (count === 1) return img(images[0], width, height);
    if (count === 2) return (
        <View style={{ flexDirection: 'row', width, height, gap }}>
            {img(images[0], (width - gap) / 2, height)}
            {img(images[1], (width - gap) / 2, height)}
        </View>
    );
    if (count === 3) return (
        <View style={{ flexDirection: 'row', width, height, gap }}>
            {img(images[0], (width - gap) * 2 / 3, height)}
            <View style={{ flex: 1, gap }}>
                {img(images[1], (width - gap) / 3, (height - gap) / 2)}
                {img(images[2], (width - gap) / 3, (height - gap) / 2)}
            </View>
        </View>
    );
    // 4+ images: 2x2 grid with overlay
    const extra = count - 4;
    return (
        <View style={{ width, height, gap }}>
            <View style={{ flex: 1, flexDirection: 'row', gap }}>
                {img(images[0], (width - gap) / 2, (height - gap) / 2)}
                {img(images[1], (width - gap) / 2, (height - gap) / 2)}
            </View>
            <View style={{ flex: 1, flexDirection: 'row', gap }}>
                {img(images[2], (width - gap) / 2, (height - gap) / 2)}
                <View style={{ width: (width - gap) / 2, height: (height - gap) / 2, position: 'relative' }}>
                    {img(images[3], (width - gap) / 2, (height - gap) / 2)}
                    {extra > 0 && (
                        <View style={[StyleSheet.absoluteFill, { backgroundColor: 'rgba(0,0,0,0.4)', alignItems: 'center', justifyContent: 'center' }]}>
                            <Text style={{ color: '#fff', fontSize: 18, fontWeight: '800' }}>+{extra}</Text>
                        </View>
                    )}
                </View>
            </View>
        </View>
    );
};

const SwipeableLightbox = ({ visible, images, onClose }) => {
    const [index, setIndex] = useState(0);
    if (!visible) return null;

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <View style={{ flex: 1, backgroundColor: '#000' }}>
                <Animated.FlatList
                    data={images}
                    horizontal
                    pagingEnabled
                    showsHorizontalScrollIndicator={false}
                    keyExtractor={(_, i) => String(i)}
                    onScroll={e => {
                        const x = e.nativeEvent.contentOffset.x;
                        setIndex(Math.round(x / Dimensions.get('window').width));
                    }}
                    renderItem={({ item }) => (
                        <View style={{ width: Dimensions.get('window').width, height: '100%', justifyContent: 'center' }}>
                            <Image source={{ uri: item }} style={{ width: '100%', height: '80%' }} resizeMode="contain" />
                        </View>
                    )}
                />
                <View style={{ position: 'absolute', top: 50, left: 20, right: 20, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <TouchableOpacity onPress={onClose} style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(0,0,0,0.5)', alignItems: 'center', justifyContent: 'center' }}>
                        <Ionicons name="close" size={24} color="#fff" />
                    </TouchableOpacity>
                    <Text style={{ color: '#fff', fontWeight: '700' }}>{index + 1} / {images.length}</Text>
                    <View style={{ width: 40 }} />
                </View>
            </View>
        </Modal>
    );
};

// ── Styles ───────────────────────────────────────────────────────────────────
const p = StyleSheet.create({
    pauseOverlay: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.2)',
    },
    pauseOrb: {
        width: 56, height: 56, borderRadius: 28,
        backgroundColor: 'rgba(0,0,0,0.5)',
        alignItems: 'center', justifyContent: 'center',
        borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.25)',
    },
    replayBtn: {
        position: 'absolute', bottom: 12, right: 12,
        flexDirection: 'row', alignItems: 'center', gap: 6,
        backgroundColor: 'rgba(0,0,0,0.6)',
        paddingHorizontal: 12, paddingVertical: 6,
        borderRadius: 20, borderWidth: 1, borderColor: 'rgba(255,255,255,0.2)',
    },
    replayText: { color: '#fff', fontSize: 12, fontWeight: '700' },
    topRight: {
        position: 'absolute', top: 10, right: 10,
        flexDirection: 'row', gap: 6,
    },
    countBadge: {
        flexDirection: 'row', alignItems: 'center', gap: 4,
        backgroundColor: 'rgba(0,0,0,0.55)',
        borderRadius: 10, paddingHorizontal: 8, paddingVertical: 4,
    },
    countText: { color: '#fff', fontSize: 11, fontWeight: '800' },
    bottomLeft: {
        position: 'absolute', bottom: 22, left: 10,
    },
    timeBadge: {
        flexDirection: 'row', alignItems: 'center', gap: 4,
        backgroundColor: 'rgba(0,0,0,0.55)',
        borderRadius: 8, paddingHorizontal: 7, paddingVertical: 3,
    },
    timeText: { color: '#fff', fontSize: 10, fontWeight: '700' },
    dotsRow: {
        position: 'absolute', bottom: 8, left: 0, right: 0,
        flexDirection: 'row', justifyContent: 'center', gap: 5,
    },
    dot: { height: 3, borderRadius: 2 },
    dotActive:   { width: 20, backgroundColor: '#fff' },
    dotInactive: { width: 6,  backgroundColor: 'rgba(255,255,255,0.35)' },
});

export default PhotoClipPlayer;
